import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  elements,
} from 'chart.js';

import {
  Header,
  Divider,
  Segment,
  Grid,
  Statistic,
  Icon,
  Button,
  Table,
  TableRow,
  TableHeaderCell,
  TableHeader,
  TableFooter,
  TableCell,
  TableBody,
} from 'semantic-ui-react';

import { sumArray } from '../../constants';
import EvaluationCard from '../evaluation_sessions/EvaluationCard';
import { BRAND } from '../../brand';
import { evaluatedOutcome, criterionPoints } from '../evaluation_sessions/evaluationRules';

const OutcomeIcon = ({ ec }) => {
  const outcome = evaluatedOutcome(ec);
  return outcome?.favorable
    ? <Icon name="check" color="green" title={outcome.label} />
    : <Icon name="close" color="red" title={outcome?.label} />;
};

// Register Charts
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  elements
);

const PartnerEvaluationComparisonPerCriteria = ({
  partner_evaluation_session,
  eos_evaluation_session,
}) => {
  // Define constructs
  const user = useSelector(state => state.auth.user);
  const labels = [partner_evaluation_session.partner.name, BRAND];
  const max_score = sumArray(
    partner_evaluation_session.partnerevaluatedcriterion_set.map(
      evaluatedcriterion => evaluatedcriterion.criterion.score
    )
  );

  // Prepare Data per Criterion
  // {
  //   id: '1f2f23a4-15c0-48d5-a16a-0aae83138c13',
  //   created_at: '2023-11-10T09:13:14.855520Z',
  //   updated_at: '2023-11-14T01:19:57.227199Z',
  //   result: {
  //     score: 2,
  //     confidence: 1,
  //     is_criterion_met: true,
  //     relevant_utterances: {
  //       sentences: [
  //         '네 안녕하세요 고객님, 워치가'
  //       ]
  //     }
  //   },
  //   partner: '4b5f668c-eef5-4c06-a228-b18fbe0be0a7',
  //   partner_evaluated_session: '03d4e9f5-7a88-4662-b3c5-03d6084164c7',
  //   criterion: {
  //     id: '21dcbd46-6bad-4b90-ac60-14684cd90cea',
  //     created_at: '2023-04-04T15:56:20.182000Z',
  //     updated_at: '2023-04-04T16:08:05.099000Z',
  //     is_deleted: false,
  //     is_postprocessing: false,
  //     is_postprocessed: true,
  //     started_at: '2023-04-04T16:08:04.231000Z',
  //     finished_at: '2023-04-04T16:08:05.099000Z',
  //     tags: 'connect',
  //     text: '친근하게  자신감 있게 진심으로가 느껴지는 초반 대화로 시작하여 응대 전반에 걸쳐 신뢰감이 유지 되었나요?',
  //     text_en: 'Did the trust remain throughout the entire conversation, starting with a friendly and confident initial conversation that felt sincere?',
  //     extra_info: '환영하기, 양해 구하기, 근처에 위치하기, 도움 줄 것을 알려주기, 공통점 찾기, 풍부한 제품 정보, 오픈된 자세, 함께 알아보기, 눈 맞추기, 목소리톤 말한 내용 이어서 대화하기, 올바른 정보 제공하기, 적합한 솔루션 추천하기, 솔직하게 말하기, 적합한 액세서리 추천하기, 진정성 있는 칭찬하기, 나의 경험이나 사례 공유 하기, 고객이 말한 내용 요약하기 등과 같이 ',
  //     extra_info_en: 'Welcoming, seeking understanding, being located nearby, indicating willingness to help, finding common ground, providing abundant product information, having an open mindset, learning together, making eye contact, continuing conversation by matching the tonality of what was said, providing accurate information, recommending suitable solutions, speaking honestly, suggesting appropriate accessories, giving genuine compliments, sharing personal experiences or examples, summarizing what the customer has said, and so on.',
  //     score: 2,
  //     int_id: 0,
  //     partner: '4b5f668c-eef5-4c06-a228-b18fbe0be0a7'
  //   },
  //   is_postprocessed: true,
  //   is_postprocessing: false
  // },

  // equivalent to zip(partner_evaluation_session.partnerevaluatedcriterion_set,eos_evaluation_session.evaluatedcriterion_set,)
  const session_criterion_sets =
    partner_evaluation_session.partnerevaluatedcriterion_set.map(
      (partner_evaluated_criterion, index) => {
        const eos_evaluated_criterion =
          eos_evaluation_session.evaluatedcriterion_set[index];
        return {
          partner_evaluated_criterion: partner_evaluated_criterion,
          eos_evaluated_criterion: eos_evaluated_criterion,
        };
      }
    );

  // Define States
  const [showInProgressResult, setShowInProgressResult] = useState(false);
  const [showCriteriaMetResult, setShowCriteriaMetResult] = useState(true);
  const [showPointsResult, setShowPointsResult] = useState(true);
  const [showCriterionResult, setShowCriterionResult] = useState(true);
  const handleToggleClick = toggle_name => {
    if (toggle_name === 'criteria_met')
      setShowCriteriaMetResult(!showCriteriaMetResult);
    else if (toggle_name === 'points') setShowPointsResult(!showPointsResult);
    else if (toggle_name === 'criterion')
      setShowCriterionResult(!showCriterionResult);
    else return;
  };

  // If the EOS Evaluation is processing, Do not show the comparison result.
  if (eos_evaluation_session.is_postprocessing && !showInProgressResult) {
    return (
      <>
        <Header as="h1" textAlign="center">
          Comparison Result
        </Header>
        <Header as="h2" textAlign="center">
          Hong Gil-dong
        </Header>

        <Divider />

        <Header as="h2" textAlign="center">
          {BRAND} 평가가 아직 처리 중입니다.
        </Header>

        {user.is_staff && (
          <div style={{ textAlign: 'center', margin: '0', marginTop: '10px' }}>
            <Button
              color="blue"
              size="huge"
              style={{ margin: 'auto' }}
              onClick={() => setShowInProgressResult(true)}
            >
              View Still In Progress Result
            </Button>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <Header as="h1" textAlign="center">
        Comparison Result
      </Header>
      <Header as="h2" textAlign="center">
        {partner_evaluation_session.chat_session.trainee.username}
      </Header>

      {/* Score comparison between manual (partner_evaluated_criterion) evaluation and EOS evaluation. */}
      
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px', 
        border: '1px solid rgba(0,0,0,0.05)', 
        overflow: 'hidden',
        marginTop: '20px'
      }}>
        <Table  striped>
          <Table.Header>
            <Table.Row style={{ backgroundColor: '#f9fafb' }}>
              <Table.HeaderCell width={8}>평가항목</Table.HeaderCell>
              <Table.HeaderCell colSpan={2} textAlign='center' style={{ borderLeft: '1px solid rgba(34,36,38,.15)' }}>
                <Icon name='apple' /> Apple
              </Table.HeaderCell>
              <Table.HeaderCell colSpan={2} textAlign='center' style={{ borderLeft: '1px solid rgba(34,36,38,.15)' }}>
                <Icon name='lightning' color='yellow' /> {BRAND}
              </Table.HeaderCell>
            </Table.Row>
            <Table.Row style={{ backgroundColor: '#f9fafb' }}>
              <Table.HeaderCell></Table.HeaderCell>
              <Table.HeaderCell width={2} textAlign="center" style={{ borderLeft: '1px solid rgba(34,36,38,.15)' }}>충족</Table.HeaderCell>
              <Table.HeaderCell width={2} textAlign="center">점수</Table.HeaderCell>
              <Table.HeaderCell width={2} textAlign="center" style={{ borderLeft: '1px solid rgba(34,36,38,.15)' }}>충족</Table.HeaderCell>
              <Table.HeaderCell width={2} textAlign="center">점수</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body> 
            {session_criterion_sets.length > 0 &&
              session_criterion_sets.map(evaluatedcriterion => {
                return (
                  <Table.Row
                    key={evaluatedcriterion.partner_evaluated_criterion.id}
                  >
                    <Table.Cell>
                      <span style={{ fontWeight: '600', marginRight: '8px' }}>
                        {evaluatedcriterion.partner_evaluated_criterion.criterion.int_id + 1}.
                      </span>
                      {evaluatedcriterion.partner_evaluated_criterion.criterion.text}
                    </Table.Cell>
                    
                    <Table.Cell textAlign="center" style={{ borderLeft: '1px solid rgba(0,0,0,0.05)' }}>
                      <OutcomeIcon ec={evaluatedcriterion.partner_evaluated_criterion} />
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      {evaluatedcriterion.partner_evaluated_criterion.result.score}
                    </Table.Cell>

                    <Table.Cell textAlign="center" style={{ borderLeft: '1px solid rgba(0,0,0,0.05)' }}>
                      <OutcomeIcon ec={evaluatedcriterion.eos_evaluated_criterion} />
                    </Table.Cell>

                    <Table.Cell textAlign="center">
                      {criterionPoints(evaluatedcriterion.eos_evaluated_criterion)}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
          </Table.Body>
        </Table>
      </div>

      {false && (
        <Grid>
          {session_criterion_sets.length > 0 &&
            session_criterion_sets.map(evaluatedcriterion => {
              return (
                <Grid.Row
                  key={evaluatedcriterion.partner_evaluated_criterion.id}
                >
                  <Grid.Column width={12}>
                    <Segment>
                      {
                        evaluatedcriterion.partner_evaluated_criterion.criterion
                          .text
                      }
                    </Segment>
                  </Grid.Column>
                  <Grid.Column width={6}></Grid.Column>
                </Grid.Row>
              );
            })}
        </Grid>
      )}
    </>
  );
};

export default PartnerEvaluationComparisonPerCriteria;
